import React, { useState, useEffect } from 'react'
import { formatNumberInput, getRawNumericValue, parseFormattedNumber } from '@/utils/numberFormat'

/**
 * NumberInput component with European formatting (. as thousand separator, , as decimal)
 * Supports both integer and decimal numbers
 */
const NumberInput = React.forwardRef(({
  value = '',
  onChange,
  onBlur,
  name,
  placeholder = '',
  className = '',
  disabled = false,
  allowDecimals = true,
  min,
  max,
  step,
  ...props
}, ref) => {
  const [displayValue, setDisplayValue] = useState('')
  const [isFocused, setIsFocused] = useState(false)

  // Initialize display value from prop value
  useEffect(() => {
    if (value !== undefined && value !== null && value !== '') {
      // If value is already formatted, use it as is
      if (typeof value === 'string' && (value.includes('.') || value.includes(','))) {
        setDisplayValue(value)
      } else {
        // Format numeric value for display
        const strValue = value.toString()
        setDisplayValue(formatNumberInput(strValue, '', allowDecimals))
      }
    } else {
      setDisplayValue('')
    }
  }, [value, allowDecimals])

  const handleInputChange = (e) => {
    const inputValue = e.target.value

    // Store the raw input to preserve user typing
    setDisplayValue(inputValue)

    // Call onChange with raw numeric value for form handling
    if (onChange) {
      const rawValue = getRawNumericValue(inputValue)

      // Create a synthetic event with the raw value
      const syntheticEvent = {
        ...e,
        target: {
          ...e.target,
          value: rawValue,
          name: name
        }
      }

      onChange(syntheticEvent)
    }
  }

  const handleFocus = (e) => {
    setIsFocused(true)
    if (props.onFocus) {
      props.onFocus(e)
    }
  }

  const handleBlur = (e) => {
    setIsFocused(false)

    // Format the value when user finishes typing
    const formatted = formatNumberInput(displayValue, '', allowDecimals)
    setDisplayValue(formatted)

    // Update form with clean value
    if (onChange) {
      const rawValue = getRawNumericValue(formatted)
      const syntheticEvent = {
        ...e,
        target: {
          ...e.target,
          value: rawValue,
          name: name
        }
      }
      onChange(syntheticEvent)
    }

    if (onBlur) {
      onBlur(e)
    }
  }

  const handleKeyDown = (e) => {
    // Allow: backspace, delete, tab, escape, enter
    if ([8, 9, 27, 13, 46].indexOf(e.keyCode) !== -1 ||
        // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
        (e.keyCode === 65 && e.ctrlKey === true) ||
        (e.keyCode === 67 && e.ctrlKey === true) ||
        (e.keyCode === 86 && e.ctrlKey === true) ||
        (e.keyCode === 88 && e.ctrlKey === true) ||
        // Allow: home, end, left, right
        (e.keyCode >= 35 && e.keyCode <= 39)) {
      return
    }

    // Allow: numbers, minus sign, period (decimal separator)
    const allowedKeys = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '-']
    if (allowDecimals) {
      allowedKeys.push('.')
    }

    if (!allowedKeys.includes(e.key)) {
      e.preventDefault()
    }

    // Only allow one decimal separator
    if (e.key === '.' && displayValue.includes('.')) {
      e.preventDefault()
    }

    // Only allow minus at the beginning
    if (e.key === '-' && (e.target.selectionStart !== 0 || displayValue.includes('-'))) {
      e.preventDefault()
    }

    if (props.onKeyDown) {
      props.onKeyDown(e)
    }
  }

  return (
    <input
      ref={ref}
      type="text"
      name={name}
      value={displayValue}
      onChange={handleInputChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      className={className}
      disabled={disabled}
      autoComplete="off"
      {...props}
    />
  )
})

NumberInput.displayName = 'NumberInput'

export default NumberInput
